import React, { useEffect } from "react";
import { Modal } from "@pd/pd-ui-kit";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  isRevokeFormFilled,
  isRevokeOrganizationFilled,
  mapRevokeFormToPayload,
} from "~/features/LicenseActionsModals/model/mapPayload";
import {
  IAssignOrganizationValue,
  ILicenseRevokeFormValues,
} from "~/features/LicenseActionsModals/model/types";
import { useLicenseActionsSubmit } from "~/features/LicenseActionsModals/model/useLicenseActionsSubmit";
import { useURLParamsSetter } from "~/hooks/useURLParamsSetter";

import AddEntityLink from "./AddEntityLink";
import AssignOrganizationBlock, {
  createEmptyPosition,
} from "./AssignOrganizationBlock";

import s from "~/features/LicenseActionsModals/LicenseActionsModals.less";

interface LicenseRevokeModalProps {
  isOpen: boolean;
}

const createEmptyOrganization = (): IAssignOrganizationValue => ({
  organization: null,
  positions: [createEmptyPosition()],
});

const createDefaultValues = (): ILicenseRevokeFormValues => ({
  organizations: [createEmptyOrganization()],
});

const LicenseRevokeModal = ({ isOpen }: LicenseRevokeModalProps) => {
  const { t } = useTranslation();
  const setParams = useURLParamsSetter();
  const { revokeLicenses } = useLicenseActionsSubmit();

  const { control, handleSubmit, reset } = useForm<ILicenseRevokeFormValues>({
    defaultValues: createDefaultValues(),
  });

  const { fields: organizationFields, append: appendOrganization } =
    useFieldArray({
      control,
      name: "organizations",
    });

  const organizationsValues =
    (useWatch<ILicenseRevokeFormValues>({ control })
      .organizations as ILicenseRevokeFormValues["organizations"]) || [];
  const isFilled = isRevokeFormFilled({
    organizations: organizationsValues,
  });

  const lastOrganization = organizationsValues[organizationsValues.length - 1];
  const canAddOrganization = Boolean(
    lastOrganization && isRevokeOrganizationFilled(lastOrganization),
  );

  const closeModal = () => {
    reset(createDefaultValues());
    setParams({ openModal: undefined });
  };

  useEffect(() => {
    if (isOpen) {
      reset(createDefaultValues());
    }
  }, [isOpen, reset]);

  const submit = handleSubmit((data) => {
    revokeLicenses.mutate(mapRevokeFormToPayload(data), {
      onSuccess: (result) => {
        if (result.revoked > 0) {
          closeModal();
        }
      },
    });
  });

  return (
    <Modal
      open={isOpen}
      onCancel={closeModal}
      title={t("licenseActionsModals.revokeTitle")}
      size="small"
      centered
      cancelButton={{
        children: t("licenseActionsModals.cancel"),
        disabled: revokeLicenses.isLoading,
        onClick: closeModal,
        bold: true,
        fontSize: "14",
        radius: 5,
      }}
      okButton={{
        children: t("licenseActionsModals.revokeSubmit"),
        onClick: submit,
        disabled: !isFilled || revokeLicenses.isLoading,
        bold: true,
        fontSize: "14",
        radius: 5,
      }}
    >
      <div className={s.form} key={isOpen ? "open" : "closed"}>
        <div className={s.blocks}>
          {organizationFields.map((organizationField, organizationIndex) => {
            const value =
              organizationsValues[organizationIndex] ||
              createEmptyOrganization();

            return (
              <AssignOrganizationBlock
                key={organizationField.id}
                control={control}
                organizationIndex={organizationIndex}
                value={value}
              />
            );
          })}
        </div>
        <div>
          <AddEntityLink
            disabled={!canAddOrganization}
            onClick={() => appendOrganization(createEmptyOrganization())}
          >
            {t("licenseActionsModals.addOrganization")}
          </AddEntityLink>
        </div>
      </div>
    </Modal>
  );
};

export default LicenseRevokeModal;
